// pages/finance/PaymentRequestDetailPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, DollarSign, FileText, Clock, User, Building, Calendar, Wallet, CreditCard, Paperclip, PieChart } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { usePaymentRequest, useApprovePaymentRequest, useRejectPaymentRequest, useConfirmPayment, useSubmitPaymentRequest } from '@/hooks/finance/usePaymentRequests';
import { useCanApproveRequest } from '@/hooks/finance/useCanApproveRequest';
import { useVatRate, useActiveWithholdingRates } from '@/hooks/finance/useTaxSettings';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { PaymentRequestStatus } from '@/types/database/payment-requests';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/currency';
import { supabase } from '@/integrations/supabase/client';
import DocumentList from '@/components/finance/DocumentList';
import { BUAllocationEditor, AllocationValue, allocationsAreValid } from '@/components/finance/BUAllocationEditor';
import { usePaymentRequestAllocations, useSavePaymentRequestAllocations } from '@/hooks/finance/usePaymentRequestAllocations';
import { PaymentRequestAllocationsBadges } from '@/components/finance/PaymentRequestAllocationsBadges';
import { DEFAULT_BUSINESS_UNIT } from '@/constants/businessUnits';

const statusConfig: Record<PaymentRequestStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: <FileText className="h-4 w-4" /> },
  pending_approval: { label: 'Pending Approval', variant: 'outline', icon: <Clock className="h-4 w-4" /> },
  approved: { label: 'Approved', variant: 'default', icon: <CheckCircle className="h-4 w-4" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="h-4 w-4" /> },
  paid: { label: 'Paid', variant: 'default', icon: <DollarSign className="h-4 w-4" /> },
  cancelled: { label: 'Cancelled', variant: 'secondary', icon: <XCircle className="h-4 w-4" /> },
};

const PaymentRequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { data: request, isLoading } = usePaymentRequest(id || '');
  const approveRequest = useApprovePaymentRequest();
  const rejectRequest = useRejectPaymentRequest();
  const confirmPayment = useConfirmPayment();
  const submitRequest = useSubmitPaymentRequest();
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paidFromAccountId, setPaidFromAccountId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  
  // VAT and Withholding state
  type VatMode = 'none' | 'add' | 'deduct';
  const [vatMode, setVatMode] = useState<VatMode>('none');
  const [withholdingApplicable, setWithholdingApplicable] = useState(false);
  const [selectedWithholdingRateId, setSelectedWithholdingRateId] = useState('');
  
  // Tax data hooks
  const { data: defaultVatRate = 15 } = useVatRate();
  const { data: withholdingRates = [] } = useActiveWithholdingRates();

  // BU Allocations
  const [editAllocations, setEditAllocations] = useState<AllocationValue[]>([]);
  const { data: loadedAllocations } = usePaymentRequestAllocations(id);
  const saveAllocations = useSavePaymentRequestAllocations();

  useEffect(() => {
    if (!paymentDialogOpen) return;
    if (loadedAllocations && loadedAllocations.length > 0) {
      setEditAllocations(loadedAllocations);
    } else {
      const bu = ((request as any)?.business_unit as string) || DEFAULT_BUSINESS_UNIT;
      setEditAllocations([{ business_unit: bu as any, percentage: 100 }]);
    }
  }, [paymentDialogOpen, loadedAllocations, request]);

  // Calculate payment totals based on VAT mode and withholding
  const paymentTotals = useMemo(() => {
    const requestAmount = request?.total_amount || 0;
    const vatRateDecimal = defaultVatRate / 100;
    
    let baseAmount: number;
    let vatAmount: number;
    let subtotal: number;
    
    switch (vatMode) {
      case 'add':
        // Amount is base, add VAT on top
        baseAmount = requestAmount;
        vatAmount = baseAmount * vatRateDecimal;
        subtotal = baseAmount + vatAmount;
        break;
      case 'deduct':
        // Amount includes VAT, extract it
        baseAmount = requestAmount / (1 + vatRateDecimal);
        vatAmount = requestAmount - baseAmount;
        subtotal = requestAmount;
        break;
      default: // 'none'
        baseAmount = requestAmount;
        vatAmount = 0;
        subtotal = requestAmount;
    }
    
    // Withholding calculated on subtotal
    const withholdingRate = withholdingApplicable && selectedWithholdingRateId
      ? withholdingRates.find(r => r.id === selectedWithholdingRateId)?.rate || 0
      : 0;
    const withholdingAmount = baseAmount * (withholdingRate / 100);
    const netPayment = subtotal - withholdingAmount;
    
    return {
      baseAmount,
      vatRate: vatMode !== 'none' ? defaultVatRate : 0,
      vatAmount,
      subtotal,
      withholdingRate,
      withholdingAmount,
      netPayment
    };
  }, [request?.total_amount, vatMode, defaultVatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

  // Parse payment reference to extract bank account and reference
  const parsePaymentReference = (ref: string | null) => {
    if (!ref) return { bankAccount: null, reference: null };
    const parts = ref.split(' | ');
    if (parts.length >= 2) {
      return { bankAccount: parts[0], reference: parts.slice(1).join(' | ') };
    }
    return { bankAccount: null, reference: ref };
  };

  // Check if current user can approve this request
  const { canApprove, reason: approvalBlockReason, isLoading: approverLoading } = useCanApproveRequest(request || null);
  
  const canConfirmPayment = request?.status === 'approved';

  const canSubmit = request?.status === 'draft' && request?.requestor_id === user?.id;

  // Finance users can reject approved requests before confirming payment
  const [userProfile, setUserProfile] = useState<{ role: string } | null>(null);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();
      setUserProfile(data);
    };
    fetchUserProfile();
  }, [user?.id]);

  const isFinanceUser = userProfile?.role === 'finance' || userProfile?.role === 'admin';
  const canRejectAsFinance = isFinanceUser && request?.status === 'approved';

  // Approver names
  const [approverNames, setApproverNames] = useState<Record<string, string>>({});
  const approverIdsKey = (request?.approvals || []).map((a: any) => a.approver_id).filter(Boolean).join(',');
  useEffect(() => {
    const ids = Array.from(new Set(approverIdsKey.split(',').filter(Boolean)));
    if (ids.length === 0) return;
    (async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, phone_number')
        .in('id', ids);
      const map: Record<string, string> = {};
      (data || []).forEach((p: any) => {
        map[p.id] = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.phone_number || 'Unknown user';
      });
      setApproverNames(map);
    })();
  }, [approverIdsKey]);

  const handleApprove = async () => {
    if (!id) return;
    await approveRequest.mutateAsync({ id, comments });
    setApproveDialogOpen(false);
    setComments('');
  };

  const handleReject = async () => {
    if (!id || !rejectionReason) return;
    await rejectRequest.mutateAsync({ id, reason: rejectionReason, comments });
    setRejectDialogOpen(false);
    setRejectionReason('');
    setComments('');
  };

  const handleConfirmPayment = async () => {
    if (!id || !paidFromAccountId) return;
    if (!allocationsAreValid(editAllocations) || editAllocations.length === 0) return;

    try {
      await saveAllocations.mutateAsync({ paymentRequestId: id, allocations: editAllocations });
    } catch {
      return;
    }

    const combinedReference = paymentReference || '';

    await confirmPayment.mutateAsync({
      id, 
      paymentReference: combinedReference, 
      actualPaymentDate: paymentDate,
      bankAccountId: paidFromAccountId,
      baseAmount: paymentTotals.baseAmount,
      vatRate: paymentTotals.vatRate,
      vatAmount: paymentTotals.vatAmount,
      totalExpense: paymentTotals.subtotal,
      withholdingRate: paymentTotals.withholdingRate,
      withholdingAmount: paymentTotals.withholdingAmount,
      netPayment: paymentTotals.netPayment,
    });
    
    setPaymentDialogOpen(false);
    setPaidFromAccountId('');
    setPaymentReference('');
    setVatMode('none');
    setWithholdingApplicable(false);
    setSelectedWithholdingRateId('');
  };

  const handleSubmit = async () => {
    if (!id) return;
    await submitRequest.mutateAsync(id);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Request not found</h2>
          <Button className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">{request.request_number}</h1>
                <Badge variant={statusConfig[request.status].variant}>
                  {statusConfig[request.status].icon}
                  <span className="ml-1">{statusConfig[request.status].label}</span>
                </Badge>
              </div>
              <p className="text-muted-foreground">{request.title}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canSubmit && (
              <Button onClick={handleSubmit}>
                Submit for Approval
              </Button>
            )}
            {canApprove && request?.status === 'pending_approval' && (
              <>
                <Button variant="destructive" onClick={() => setRejectDialogOpen(true)}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => setApproveDialogOpen(true)}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            {!canApprove && request?.status === 'pending_approval' && !approverLoading && (
              <Badge variant="outline" className="text-muted-foreground">
                {approvalBlockReason || 'Awaiting approval from designated approver'}
              </Badge>
            )}
            {canConfirmPayment && (
              <>
                {canRejectAsFinance && (
                  <Button variant="destructive" onClick={() => setRejectDialogOpen(true)}>
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                )}
                <Button onClick={() => setPaymentDialogOpen(true)}>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Confirm Payment
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {request.description && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground">Description</h4>
                    <p className="mt-1">{request.description}</p>
                  </div>
                )}

                <Separator />

                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Line Items</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-3">Description</th>
                          <th className="text-right p-3">Qty</th>
                          <th className="text-right p-3">Unit Price</th>
                          <th className="text-right p-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {request.items?.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="p-3">{item.description}</td>
                            <td className="text-right p-3">{item.quantity}</td>
                            <td className="text-right p-3">{formatCurrency(item.unit_price, request.currency)}</td>
                            <td className="text-right p-3">{formatCurrency(item.amount, request.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted/50">
                        <tr className="border-t">
                          <td colSpan={3} className="p-3 text-right font-medium">Subtotal</td>
                          <td className="p-3 text-right">{formatCurrency(request.amount, request.currency)}</td>
                        </tr>
                        {request.vat_applicable && (
                          <tr>
                            <td colSpan={3} className="p-3 text-right font-medium">VAT (15%)</td>
                            <td className="p-3 text-right">{formatCurrency(request.vat_amount, request.currency)}</td>
                          </tr>
                        )}
                        <tr className="font-semibold">
                          <td colSpan={3} className="p-3 text-right">Total</td>
                          <td className="p-3 text-right">{formatCurrency(request.total_amount, request.currency)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Details - shown for paid requests */}
            {request.status === 'paid' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-500" />
                    Payment Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 sm:grid-cols-2">
                    {/* Tax Breakdown */}
                    {request.expense && request.expense[0] && (
                      <div className="space-y-2 text-sm bg-muted/50 rounded-lg p-4">
                        <h4 className="font-medium mb-3">Financial Summary</h4>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Base Amount</span>
                          <span>{formatCurrency(request.expense[0].amount, request.currency)}</span>
                        </div>
                        {request.expense[0].vat_rate && request.expense[0].vat_rate > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">VAT ({request.expense[0].vat_rate}%)</span>
                            <span>+ {formatCurrency(request.expense[0].vat_amount || 0, request.currency)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-medium pt-2 border-t border-border">
                          <span>Total Expense</span>
                          <span>{formatCurrency(request.expense[0].total_amount, request.currency)}</span>
                        </div>
                        {request.expense[0].withholding_rate && request.expense[0].withholding_rate > 0 && (
                          <>
                            <div className="flex justify-between text-muted-foreground">
                              <span>Withholding ({request.expense[0].withholding_rate}%)</span>
                              <span>- {formatCurrency(request.expense[0].withholding_amount || 0, request.currency)}</span>
                            </div>
                            <div className="flex justify-between font-semibold pt-2 border-t border-border">
                              <span>Net Payment</span>
                              <span>{formatCurrency(request.expense[0].net_payment || request.expense[0].total_amount, request.currency)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Payment Info */}
                    <div className="space-y-4">
                      {request.actual_payment_date && (
                        <div className="flex items-center gap-3">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Payment Date</p>
                            <p className="font-medium">{format(new Date(request.actual_payment_date), 'MMM d, yyyy')}</p>
                          </div>
                        </div>
                      )}
                      {(() => {
                        const { bankAccount, reference } = parsePaymentReference(request.payment_reference);
                        return (
                          <>
                            {bankAccount && (
                              <div className="flex items-center gap-3">
                                <Wallet className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="text-sm text-muted-foreground">Paid From</p>
                                  <p className="font-medium">{bankAccount}</p>
                                </div>
                              </div>
                            )}
                            {reference && (
                              <div className="flex items-center gap-3">
                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                                <div>
                                  <p className="text-sm text-muted-foreground">Reference</p>
                                  <p className="font-medium">{reference}</p>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {request.paid_at && (
                        <div className="text-xs text-muted-foreground pt-3 border-t">
                          Confirmed on {format(new Date(request.paid_at), 'MMM d, yyyy \'at\' HH:mm')}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Requestor</p>
                    <p className="font-medium">
                      {request.requester_first_name && request.requester_last_name 
                        ? `${request.requester_first_name} ${request.requester_last_name}` 
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="font-medium">{request.department?.name || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{request.category?.name || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Preferred Payment Date</p>
                    <p className="font-medium">
                      {request.preferred_payment_date 
                        ? format(new Date(request.preferred_payment_date), 'MMM d, yyyy')
                        : 'Not specified'}
                    </p>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(request.total_amount, request.currency)}</p>
                </div>
                <Separator />
                <div className="flex items-start gap-3">
                  <PieChart className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <PaymentRequestAllocationsBadges
                    paymentRequestId={request.id}
                    fallbackBusinessUnit={(request as any).business_unit}
                    label="Business Unit Allocation"
                  />
                </div>
              </CardContent>
            </Card>

            {request.vendor_name && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Vendor Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-medium">{request.vendor_name}</p>
                  {request.vendor_bank_name && (
                    <div className="text-sm text-muted-foreground">
                      <p>{request.vendor_bank_name}</p>
                      <p>A/C: {request.vendor_bank_account}</p>
                      {request.vendor_bank_branch && <p>Branch: {request.vendor_bank_branch}</p>}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {request.approvals && request.approvals.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Approval History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {request.approvals.map((approval) => (
                      <div key={approval.id} className="flex items-start gap-3">
                        {approval.status === 'approved' ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mt-1" />
                        ) : (
                          <XCircle className="h-4 w-4 text-destructive mt-1" />
                        )}
                        <div>
                          <p className="text-sm font-medium">
                            Level {approval.approval_level} - {approval.status}
                          </p>
                          <p className="text-sm">
                            {approval.status === 'approved' ? 'Approved by' : 'Rejected by'}{' '}
                            <span className="font-medium">{approverNames[(approval as any).approver_id] || '—'}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(approval.approved_at), 'MMM d, yyyy HH:mm')}
                          </p>
                          {approval.comments && (
                            <p className="text-sm mt-1">{approval.comments}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            {request.documents && request.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Documents ({request.documents.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DocumentList
                    documents={request.documents.map(doc => ({
                      id: doc.id,
                      file_name: doc.file_name,
                      file_path: doc.file_path,
                      document_type: doc.document_type,
                      file_size: doc.file_size,
                      created_at: doc.created_at,
                    }))}
                    bucketName="finance-documents"
                    canDelete={false}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Payment Request</DialogTitle>
            <DialogDescription>
              You are about to approve this payment request for {formatCurrency(request.total_amount, request.currency)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Comments (optional)</Label>
              <Textarea
                placeholder="Add any comments..."
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={approveRequest.isPending}>
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Payment Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                placeholder="Enter the reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleReject} 
              disabled={!rejectionReason || rejectRequest.isPending}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>
              Apply taxes and confirm payment details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Request Amount */}
            <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
              <span className="text-sm font-medium">Request Amount</span>
              <span className="font-semibold">{formatCurrency(request.total_amount, request.currency)}</span>
            </div>

            {/* VAT Mode Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">VAT Handling</Label>
              <RadioGroup value={vatMode} onValueChange={(v) => setVatMode(v as typeof vatMode)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="vat-none" />
                  <Label htmlFor="vat-none" className="text-sm font-normal cursor-pointer">No VAT</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="add" id="vat-add" />
                  <Label htmlFor="vat-add" className="text-sm font-normal cursor-pointer">
                    Add VAT ({defaultVatRate}%) – amount is base
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="deduct" id="vat-deduct" />
                  <Label htmlFor="vat-deduct" className="text-sm font-normal cursor-pointer">
                    Deduct VAT ({defaultVatRate}%) – amount includes VAT
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Tax Breakdown */}
            <div className="space-y-2 text-sm border rounded-lg p-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Base Amount</span>
                <span>{formatCurrency(paymentTotals.baseAmount, request.currency)}</span>
              </div>
              {vatMode !== 'none' && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VAT ({defaultVatRate}%)</span>
                  <span className="text-green-600">+{formatCurrency(paymentTotals.vatAmount, request.currency)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-medium">
                <span>Subtotal (Total Expense)</span>
                <span>{formatCurrency(paymentTotals.subtotal, request.currency)}</span>
              </div>
            </div>

            {/* Withholding Tax */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Apply Withholding Tax</Label>
                <Switch
                  checked={withholdingApplicable}
                  onCheckedChange={setWithholdingApplicable}
                />
              </div>
              {withholdingApplicable && (
                <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select withholding rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {withholdingRates.map((rate) => (
                      <SelectItem key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Withholding & Net Payment */}
            {withholdingApplicable && selectedWithholdingRateId && (
              <div className="space-y-2 text-sm border rounded-lg p-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Withholding ({paymentTotals.withholdingRate}%)</span>
                  <span className="text-destructive">-{formatCurrency(paymentTotals.withholdingAmount, request.currency)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Net Payment to Vendor</span>
                  <span>{formatCurrency(paymentTotals.netPayment, request.currency)}</span>
                </div>
              </div>
            )}

            {/* Final Net Payment if no withholding shown yet */}
            {(!withholdingApplicable || !selectedWithholdingRateId) && (
              <div className="flex justify-between items-center p-3 bg-primary/10 rounded-lg font-semibold">
                <span>Net Payment to Vendor</span>
                <span>{formatCurrency(paymentTotals.netPayment, request.currency)}</span>
              </div>
            )}

            <Separator />

            {/* Payment Details */}
            <div className="space-y-2">
              <Label>Paid From Account *</Label>
              <Select value={paidFromAccountId} onValueChange={setPaidFromAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder={bankAccountsLoading ? "Loading..." : "Select bank account"} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment Reference</Label>
              <Input
                placeholder="Transaction ID or reference number"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </div>
            <Separator />
            <div className="rounded-lg border p-3">
              <BUAllocationEditor value={editAllocations} onChange={setEditAllocations} compact />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmPayment}
              disabled={
                !paidFromAccountId ||
                confirmPayment.isPending ||
                saveAllocations.isPending ||
                !allocationsAreValid(editAllocations) ||
                editAllocations.length === 0
              }
            >
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default PaymentRequestDetailPage;
